import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, TextField, MenuItem
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import api from '../../../api/axiosInterceptor';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';
import { LoadingSpinner } from '../../../Components/spinner/spinner';

export const AdminFeRoleMasterComponent = ({
  title,
  apiEndpoint,
  roleFieldName,      // ac / tahsildar / js / legal
  roleLabel           // AC / Tahsildar / JS / Legal
}) => {

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feBaseList, setFeBaseList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    [roleFieldName]: '',
    is_active: true,
  });

  const [formErrors, setFormErrors] = useState({});
  const [userSearch, setUserSearch] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // ------------------- FETCH -------------------

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mainRes, feBaseRes, userRes] = await Promise.all([
        api.get(`/fe-mgmt/${apiEndpoint}/`),
        api.get(`/fe-mgmt/fe-base/`),
        api.get(`/auth/role-mgmt/`)
      ]);

      const feBaseMap = new Map(feBaseRes.data.map(fe => [fe.id, fe.name_of_fe]));
      const userMap = new Map(userRes.data.map(u => [u.id, u.email]));

      const processedData = mainRes.data.map((item, index) => ({
        id: item.id,
        slNo: index + 1,
        ...item,
        fe_base_name: feBaseMap.get(item.financial_establishment) || 'N/A',
        role_email: userMap.get(item[roleFieldName]) || 'N/A'
      }));

      setRows(processedData);
      setFeBaseList(feBaseRes.data);
      setUserList(userRes.data);

    } catch {
      showSnackbar(`Failed to fetch ${roleLabel} records.`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ------------------- COLUMNS -------------------

  const columns = [
    { field: 'slNo', headerName: 'Sl No.', width: 70 },
    {
      field: 'financial_establishment',
      headerName: 'FE Base',
      width: 200,
      renderCell: (params) => params.row.fe_base_name
    },
    {
      field: roleFieldName,
      headerName: `${roleLabel} Email`,
      width: 230,
      renderCell: (params) => params.row.role_email
    },
    {
      field: 'is_active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (params.value ? 'Yes' : 'No')
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)}>
            <Delete />
          </IconButton>
        </>
      )
    }
  ];

  // ------------------- HANDLERS -------------------

  const showSnackbar = (msg, severity) => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setFormData({
      financial_establishment: '',
      [roleFieldName]: '',
      is_active: true
    });
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    setFormData({
      financial_establishment: item.financial_establishment,
      [roleFieldName]: item[roleFieldName],
      is_active: item.is_active
    });
    setOpenFormDialog(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment)
      errors.financial_establishment = 'FE Base is required';
    if (!formData[roleFieldName])
      errors[roleFieldName] = `${roleLabel} is required`;
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditMode) {
        await api.patch(`/fe-mgmt/${apiEndpoint}/${selectedItem.id}/`, formData);
        showSnackbar(`${roleLabel} updated successfully`, 'success');
      } else {
        await api.post(`/fe-mgmt/${apiEndpoint}/`, formData);
        showSnackbar(`${roleLabel} created successfully`, 'success');
      }
      fetchData();
      setOpenFormDialog(false);
    } catch {
      showSnackbar('Save failed.', 'error');
    }
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/fe-mgmt/${apiEndpoint}/${deleteItem.id}/`);
      showSnackbar('Deleted successfully.', 'success');
      fetchData();
    } catch {
      showSnackbar('Delete failed.', 'error');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const filteredUsers = userList.filter(u =>
    u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ mt: 8 }}>
      <Box display="flex" justifyContent="space-between" p={2}>
        <Typography variant="h4">{title}</Typography>
        <Button variant="contained" onClick={handleAdd}>
          Add {roleLabel}
        </Button>
      </Box>

      <TableComponent columns={columns} rows={rows} />

      {/* Form Dialog */}
      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} fullWidth>
        <DialogTitle>{isEditMode ? `Edit ${roleLabel}` : `Add ${roleLabel}`}</DialogTitle>
        <DialogContent>

          <TextField
            select
            label="FE Base"
            fullWidth
            margin="normal"
            value={formData.financial_establishment}
            onChange={(e) =>
              setFormData({ ...formData, financial_establishment: e.target.value })
            }
          >
            {feBaseList.map(fe => (
              <MenuItem key={fe.id} value={fe.id}>
                {fe.name_of_fe}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={`Search ${roleLabel} by Email`}
            fullWidth
            margin="normal"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />

          <TextField
            select
            label={`${roleLabel} Email`}
            fullWidth
            margin="normal"
            value={formData[roleFieldName]}
            onChange={(e) =>
              setFormData({ ...formData, [roleFieldName]: e.target.value })
            }
          >
            {filteredUsers.map(user => (
              <MenuItem key={user.id} value={user.id}>
                {user.email}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Active"
            fullWidth
            margin="normal"
            value={formData.is_active ? 'true' : 'false'}
            onChange={(e) =>
              setFormData({ ...formData, is_active: e.target.value === 'true' })
            }
          >
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </TextField>

        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenFormDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};